import type { StaffRole, UserDetail } from '@planuze/admin-contracts';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@planuze/ui';
import { KeyRound, MoreHorizontal } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useUsersContent } from '../../../content/sections/users.js';
import { fmtDate } from '../../../lib/format.js';
import { EMPTY_VALUE } from '../../detail-fields.js';
import { FacetMultiSelect } from '../../filters/facet-multi-select.js';
import { SegmentedControl } from '../../filters/segmented-control.js';
import { IssueManualLicenseModal } from '../modals/issue-manual-license-modal.js';
import { RevokeLicenseModal } from '../modals/revoke-license-modal.js';
import { TransferLicenseModal } from '../modals/transfer-license-modal.js';

type License = UserDetail['licenses'][number];
const MANAGE_ROLES: readonly StaffRole[] = ['super_admin', 'support'];

/* Vigência derivada de `expiresAt` — fonte única para coluna e filtro: sem data = perpétua;
   data futura = ativa; data passada = expirada. */
type Vigencia = 'ativa' | 'expirada' | 'perpetua';
function licenseVigencia(license: License, now: number): Vigencia {
  if (license.expiresAt === null) return 'perpetua';
  return new Date(license.expiresAt).getTime() > now ? 'ativa' : 'expirada';
}

type VigenciaFilter = 'todas' | Vigencia;

interface Props {
  readonly licenses: readonly License[];
  readonly userId: string;
  readonly staffRole: StaffRole;
}

type RowAction = { readonly kind: 'revoke' | 'transfer'; readonly packId: string };

export function LicensesTab({ licenses, userId, staffRole }: Props) {
  const copy = useUsersContent();
  const canManage = MANAGE_ROLES.includes(staffRole);
  const canIssue = staffRole === 'super_admin';
  const [action, setAction] = useState<RowAction | null>(null);
  const [issueOpen, setIssueOpen] = useState(false);
  const [vigencia, setVigencia] = useState<VigenciaFilter>('todas');
  const [kinds, setKinds] = useState<string[]>([]);

  /* Opções de tipo deduplicadas dos `kind` presentes (ignora nulos — o DTO permite kind null). */
  const kindOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const license of licenses) {
      if (license.kind !== null) seen.add(license.kind);
    }
    return [...seen].sort().map((kind) => ({ value: kind, label: kind }));
  }, [licenses]);

  const { rows, counts } = useMemo(() => {
    const now = Date.now();
    const withVigencia = licenses.map((license) => ({
      license,
      vigencia: licenseVigencia(license, now),
    }));
    return {
      counts: {
        todas: withVigencia.length,
        ativa: withVigencia.filter((r) => r.vigencia === 'ativa').length,
        expirada: withVigencia.filter((r) => r.vigencia === 'expirada').length,
        perpetua: withVigencia.filter((r) => r.vigencia === 'perpetua').length,
      },
      rows: withVigencia.filter(
        (r) =>
          (vigencia === 'todas' || r.vigencia === vigencia) &&
          (kinds.length === 0 || (r.license.kind !== null && kinds.includes(r.license.kind))),
      ),
    };
  }, [licenses, vigencia, kinds]);

  const clearFilters = () => {
    setVigencia('todas');
    setKinds([]);
  };

  return (
    <div className="space-y-3">
      {canIssue && (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setIssueOpen(true)}>
            {copy.licensesTab.issueButton}
          </Button>
        </div>
      )}
      {licenses.length === 0 ? (
        <EmptyState icon={<KeyRound className="size-6" />} title={copy.licensesTab.empty} />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <SegmentedControl<VigenciaFilter>
              ariaLabel={copy.licensesTab.filterAriaLabel}
              value={vigencia}
              onChange={setVigencia}
              options={[
                { value: 'todas', label: copy.licensesTab.optionAll, count: counts.todas },
                { value: 'ativa', label: copy.licensesTab.optionActive, count: counts.ativa },
                {
                  value: 'expirada',
                  label: copy.licensesTab.optionExpired,
                  count: counts.expirada,
                },
                {
                  value: 'perpetua',
                  label: copy.licensesTab.optionPerpetual,
                  count: counts.perpetua,
                },
              ]}
            />
            <FacetMultiSelect
              label={copy.licensesTab.typeFacetLabel}
              values={kinds}
              onChange={setKinds}
              options={kindOptions}
            />
          </div>
          {rows.length === 0 ? (
            <EmptyState
              icon={<KeyRound className="size-6" />}
              title={copy.common.noResultsTitle}
              description={copy.licensesTab.emptyFilteredDescription}
              primaryAction={
                <Button variant="outline" onClick={clearFilters}>
                  {copy.common.clearFilters}
                </Button>
              }
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{copy.licensesTab.columnPack}</TableHead>
                  <TableHead>{copy.licensesTab.columnType}</TableHead>
                  <TableHead>{copy.licensesTab.columnVersion}</TableHead>
                  <TableHead>{copy.licensesTab.columnAcquired}</TableHead>
                  <TableHead>{copy.licensesTab.columnExpires}</TableHead>
                  {canManage && <TableHead />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(({ license }) => (
                  <TableRow key={license.packId}>
                    <TableCell className="text-foreground">{license.packId}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {license.kind ?? EMPTY_VALUE}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{license.version}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {fmtDate(license.acquiredAt)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {license.expiresAt === null
                        ? copy.licensesTab.perpetualValue
                        : fmtDate(license.expiresAt)}
                    </TableCell>
                    {canManage && (
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={copy.licensesTab.actionsAriaLabel}
                            >
                              <MoreHorizontal className="size-4" aria-hidden="true" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem
                              onClick={() => setAction({ kind: 'revoke', packId: license.packId })}
                            >
                              {copy.licensesTab.revokeAction}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                setAction({ kind: 'transfer', packId: license.packId })
                              }
                            >
                              {copy.licensesTab.transferAction}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}
      {action?.kind === 'revoke' && (
        <RevokeLicenseModal
          open
          onClose={() => setAction(null)}
          userId={userId}
          packId={action.packId}
        />
      )}
      {action?.kind === 'transfer' && (
        <TransferLicenseModal
          open
          onClose={() => setAction(null)}
          userId={userId}
          packId={action.packId}
        />
      )}
      {issueOpen && (
        <IssueManualLicenseModal open onClose={() => setIssueOpen(false)} userId={userId} />
      )}
    </div>
  );
}
